import React, { useState } from 'react';
import { TravauxSupplementaires, StatutTS } from '../types';
import TravauxSupplementairesRow from './TravauxSupplementairesRow';
import { PlusIcon } from '@heroicons/react/24/outline';

interface TravauxSupplementairesSectionProps {
  travauxSupplementaires: TravauxSupplementaires[];
  onAjouter: (ts: {
    intitule: string;
    montant: number;
    commentaire?: string;
    dateAjout: string;
    statut: StatutTS;
  }) => void;
}

const TravauxSupplementairesSection: React.FC<TravauxSupplementairesSectionProps> = ({
  travauxSupplementaires,
  onAjouter,
}) => {
  const [showForm, setShowForm] = useState(false);
  const [intitule, setIntitule] = useState('');
  const [montant, setMontant] = useState('');
  const [commentaire, setCommentaire] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const montantNum = parseFloat(montant.replace(/\s/g, '').replace(',', '.'));
    if (!intitule.trim() || isNaN(montantNum) || montantNum <= 0) return;

    onAjouter({
      intitule: intitule.trim(),
      montant: montantNum,
      commentaire: commentaire.trim() || undefined,
      dateAjout: new Date().toISOString().split('T')[0],
      statut: 'en_attente',
    });

    setIntitule('');
    setMontant('');
    setCommentaire('');
    setShowForm(false);
  };

  const fmt = (n: number) => n.toLocaleString('fr-FR');
  const totalTS = travauxSupplementaires
    .filter(ts => ts.statut === 'valide')
    .reduce((s, ts) => s + ts.montant, 0);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <h4 className="text-sm font-semibold text-gray-700">Travaux supplémentaires</h4>
          {totalTS > 0 && (
            <span className="text-xs text-red-600 font-medium">
              Total validé : {fmt(totalTS)} €
            </span>
          )}
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center text-xs text-violet-600 hover:text-violet-800 font-medium transition-colors"
        >
          <PlusIcon className="w-4 h-4 mr-1" />
          Ajouter
        </button>
      </div>

      {/* Formulaire inline */}
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-violet-50 border border-violet-200 rounded-lg p-4 mb-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            <input
              type="text"
              value={intitule}
              onChange={(e) => setIntitule(e.target.value)}
              placeholder="Intitulé des travaux"
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
              required
            />
            <input
              type="text"
              value={montant}
              onChange={(e) => setMontant(e.target.value)}
              placeholder="Montant (€)"
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
              required
            />
            <input
              type="text"
              value={commentaire}
              onChange={(e) => setCommentaire(e.target.value)}
              placeholder="Commentaire (optionnel)"
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 rounded-lg px-3 py-1.5 text-sm transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="bg-violet-600 text-white hover:bg-violet-700 rounded-lg px-3 py-1.5 text-sm transition-colors"
            >
              Ajouter
            </button>
          </div>
        </form>
      )}

      {/* Tableau TS */}
      {travauxSupplementaires.length > 0 ? (
        <table className="w-full">
          <thead>
            <tr className="text-left text-xs text-gray-500 uppercase tracking-wider">
              <th className="py-2 px-3">Intitulé</th>
              <th className="py-2 px-3 text-right">Montant</th>
              <th className="py-2 px-3">Date</th>
              <th className="py-2 px-3">Statut</th>
              <th className="py-2 px-3">Commentaire</th>
            </tr>
          </thead>
          <tbody>
            {travauxSupplementaires.map(ts => (
              <TravauxSupplementairesRow key={ts.id} ts={ts} />
            ))}
          </tbody>
        </table>
      ) : (
        <p className="text-sm text-gray-400 italic">Aucun travaux supplémentaire</p>
      )}
    </div>
  );
};

export default TravauxSupplementairesSection;
